import { CaptionStyles } from "../types";
import { CaptionStylePreset } from "../types/caption-styles";

/**
 * Default caption style used when no preset is selected or for new projects.
 * This is the single source of truth for caption styling defaults.
 */
export const DEFAULT_CAPTION_STYLE: CaptionStyles = {
  fontFamily: "Inter, sans-serif",
  fontSize: "2.2rem",
  lineHeight: 1.4,
  textAlign: "center",
  color: "#000000",
  textShadow: "2px 2px 4px rgba(0,0,0,0.6), 0 0 20px rgba(0,0,0,0.3)",
  padding: "12px",
  fontWeight: 500,
  letterSpacing: "0.01em",
  highlightStyle: {
    backgroundColor: "rgba(59, 130, 246, 0.92)",
    color: "#FFFFFF",
    scale: 1.06,
    fontWeight: 600,
    textShadow: "2px 2px 4px rgba(0,0,0,0.4)",
    borderRadius: "6px",
    padding: "2px 8px",
  },
};

/**
 * Built-in presets - 5 curated caption style configurations.
 * These are always available and cannot be deleted by users.
 */
export const BUILT_IN_PRESETS: CaptionStylePreset[] = [
  {
    id: "classic",
    name: "Classic",
    isBuiltIn: true,
    styles: {
      fontFamily: "Inter, sans-serif",
      fontSize: "2.2rem",
      lineHeight: 1.4,
      textAlign: "center",
      color: "#000000",
      textShadow: "2px 2px 4px rgba(0,0,0,0.6), 0 0 20px rgba(0,0,0,0.3)",
      padding: "12px",
      fontWeight: 500,
      letterSpacing: "0.01em",
      highlightStyle: {
        backgroundColor: "rgba(59, 130, 246, 0.92)",
        color: "#FFFFFF",
        scale: 1.06,
        fontWeight: 600,
        textShadow: "2px 2px 4px rgba(0,0,0,0.4)",
        borderRadius: "6px",
        padding: "2px 8px",
      },
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    isBuiltIn: true,
    styles: {
      fontFamily: "Inter, sans-serif",
      fontSize: "1.8rem",
      lineHeight: 1.3,
      textAlign: "center",
      color: "#000000",
      fontWeight: 500,
      letterSpacing: "0.01em",
      highlightStyle: {
        backgroundColor: "#FFD93D",
        color: "#000000",
        scale: 1.04,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: "4px",
      },
    },
  },
  {
    id: "bold",
    name: "Bold",
    isBuiltIn: true,
    styles: {
      fontFamily: "Montserrat, sans-serif",
      fontSize: "2.2rem",
      lineHeight: 1.3,
      textAlign: "center",
      color: "#000000",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      textShadow: "3px 3px 0 rgba(0,0,0,0.8)",
      highlightStyle: {
        backgroundColor: "#FF3B3B",
        color: "#FFFFFF",
        scale: 1.08,
        fontWeight: 800,
        textShadow: "3px 3px 0 rgba(0,0,0,0.8)",
        borderRadius: "2px",
        padding: "4px 12px",
      },
    },
  },
  {
    id: "neon",
    name: "Neon",
    isBuiltIn: true,
    styles: {
      fontFamily: "Outfit, sans-serif",
      fontSize: "2.2rem",
      lineHeight: 1.3,
      textAlign: "center",
      color: "#000000",
      textShadow:
        "0 0 10px rgba(255,255,255,0.9), 0 0 20px rgba(255,255,255,0.5), 0 0 30px rgba(255,255,255,0.3)",
      highlightStyle: {
        backgroundColor: "rgba(139, 92, 246, 0.25)",
        color: "#F0ABFC",
        scale: 1.07,
        fontWeight: 600,
        textShadow:
          "0 0 10px rgba(240,171,252,0.9), 0 0 20px rgba(240,171,252,0.5), 0 0 30px rgba(240,171,252,0.3)",
        borderRadius: "8px",
        padding: "4px 16px",
      },
    },
  },
  {
    id: "handwritten",
    name: "Handwritten",
    isBuiltIn: true,
    styles: {
      fontFamily: "Caveat, cursive",
      fontSize: "2.4rem",
      lineHeight: 1.4,
      textAlign: "center",
      color: "#000000",
      textShadow: "1px 1px 2px rgba(0,0,0,0.6)",
      fontWeight: 400,
      padding: "8px",
      highlightStyle: {
        backgroundColor: "rgba(34, 197, 94, 0.9)",
        color: "#FFFFFF",
        scale: 1.08,
        fontWeight: 600,
        textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
        borderRadius: "8px",
        padding: "2px 10px",
      },
    },
  },
];

/**
 * Text shadow presets for the effects settings
 */
export const TEXT_SHADOW_PRESETS = {
  none: "",
  soft: "1px 1px 3px rgba(0,0,0,0.4)",
  hard: "2px 2px 0 rgba(0,0,0,0.8)",
  glow: "0 0 10px rgba(255,255,255,0.9), 0 0 20px rgba(255,255,255,0.5)",
} as const;

export type TextShadowPresetKey = keyof typeof TEXT_SHADOW_PRESETS;

/**
 * @deprecated Use DEFAULT_CAPTION_STYLE and BUILT_IN_PRESETS instead.
 * This is kept temporarily for backwards compatibility during migration.
 */
export const captionTemplates: Record<
  string,
  { name: string; styles: CaptionStyles; preview: string; isPro?: boolean }
> = {
  classic: {
    name: "Classic",
    preview: "Clean & Professional",
    styles: BUILT_IN_PRESETS[0].styles,
  },
  minimal: {
    name: "Minimal",
    preview: "Simple & Clean",
    styles: BUILT_IN_PRESETS[1].styles,
  },
  bold: {
    name: "Bold",
    preview: "Bold & Dynamic",
    styles: BUILT_IN_PRESETS[2].styles,
  },
  neon: {
    name: "Neon",
    preview: "Vibrant & Electric",
    styles: BUILT_IN_PRESETS[3].styles,
  },
  handwritten: {
    name: "Handwritten",
    preview: "Casual & Friendly",
    styles: BUILT_IN_PRESETS[4].styles,
  },
};
